import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT, statusLabel } from "@/lib/i18n";
import { BILL_DENOMS, COIN_DENOMS } from "@/lib/money";
import { StatusBanner } from "@/components/StatusDisplay";

const NoteBlock = ({ text }) => {
  const { user } = useAuth();
  const t = useT();
  const [translated, setTranslated] = useState(null);
  const [showing, setShowing] = useState("original");
  const [busy, setBusy] = useState(false);
  const lang = user?.language || "en";
  const doTranslate = async () => {
    if (translated) { setShowing("translated"); return; }
    setBusy(true);
    try {
      const r = await api.translate(text, lang);
      setTranslated(r.translated);
      setShowing("translated");
    } catch { /* keep original */ }
    finally { setBusy(false); }
  };
  return (
    <Card className="rounded-2xl border-border p-6">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("notes")}</p>
        {showing === "original" ? (
          <button onClick={doTranslate} disabled={busy} data-testid="translate-note-btn" className="text-xs font-semibold text-primary hover:underline disabled:opacity-50">
            {busy ? t("translating") : t("translate")}
          </button>
        ) : (
          <button onClick={() => setShowing("original")} data-testid="view-original-btn" className="text-xs font-semibold text-primary hover:underline">
            {t("view_original")}
          </button>
        )}
      </div>
      <p className="whitespace-pre-wrap text-sm text-foreground">{showing === "translated" ? translated : text}</p>
    </Card>
  );
};

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(n || 0).toFixed(2)}`;

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={strong ? "text-sm font-bold text-foreground" : "text-sm text-muted-foreground"}>{label}</span>
    <span className={`tabular-nums ${strong ? "font-display text-lg font-black text-foreground" : "text-sm font-semibold text-foreground"}`}>{value}</span>
  </div>
);

export default function CountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCount(id).then(setCount).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Skeleton className="h-96 w-full rounded-2xl" />;
  if (!count)
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">{t("count_not_found")}</p>
        <Button onClick={() => navigate("/history")}>{t("back_to_history")}</Button>
      </div>
    );

  const overShort = count.over_short ?? count.difference ?? 0;
  const overShortCents = Math.round(overShort * 100);
  const deposit = count.deposit_amount ?? count.cash_to_deposit ?? 0;
  const totalCash = count.total_cash_counted ?? count.actual_counted ?? 0;
  const d = new Date(count.created_at);
  const sd = count.starting_denoms || {};
  const cd = count.closing_denoms || {};
  const hasDenoms = count.starting_denoms || count.closing_denoms;

  return (
    <div className="space-y-5">
      <Button variant="ghost" onClick={() => navigate("/history")} data-testid="back-to-history-btn" className="-ml-2 h-10 text-muted-foreground">
        <ArrowLeft className="mr-2 h-4 w-4" /> {t("nav_history")}
      </Button>

      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">{count.employee_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          {" · "}
          {d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
        </p>
        {count.created_by_name && (
          <p className="mt-1 text-sm font-semibold text-muted-foreground" data-testid="detail-author">{t("counted_by")} {count.created_by_name}</p>
        )}
      </div>

      <StatusBanner status={count.status} differenceCents={overShortCents} testid="detail-status-banner" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="rounded-2xl border-border p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("total_cash_counted")}</p>
          <p className="mt-1 font-display text-3xl font-black tabular-nums text-foreground" data-testid="detail-actual">{fmt(totalCash)}</p>
        </Card>
        <Card className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">{t("deposit_amount")}</p>
          <p className="mt-1 font-display text-3xl font-black tabular-nums text-primary" data-testid="detail-deposit">{fmt(deposit)}</p>
        </Card>
      </div>

      <Card className="rounded-2xl border-border p-6">
        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("drawer_reconciliation")}</p>
        <Row label={t("starting_amount")} value={fmt(count.starting_amount ?? count.starting_cash)} />
        {hasDenoms && <Row label={t("starting_bills")} value={fmt(count.starting_bills)} />}
        {hasDenoms && <Row label={t("starting_coins")} value={fmt(count.starting_coins)} />}
        <Row label={t("expected_cash")} value={fmt(count.expected_cash)} />
        {count.adjustments?.length > 0 && (
          <div className="my-2 border-t border-border pt-2">
            {count.adjustments.map((a, i) => <Row key={i} label={a.description} value={`−${fmt(a.amount)}`} />)}
          </div>
        )}
        <Row label={t("payouts_removed")} value={`−${fmt(count.total_adjustments)}`} />
        <div className="border-t border-border pt-1"><Row label={t("expected_drawer")} value={fmt(count.expected_drawer ?? count.final_expected)} strong /></div>
        <div className="mt-1 border-t border-border pt-1">
          {hasDenoms && <Row label={t("total_bills_counted")} value={fmt(count.total_bills_counted)} />}
          {hasDenoms && <Row label={t("total_coins_counted")} value={fmt(count.total_coins_counted)} />}
          <Row label={t("total_cash_counted")} value={fmt(totalCash)} strong />
          <Row label={t("over_short")} value={count.status === "BALANCED" ? t("balanced") : `${statusLabel(t, count.status)} ${fmt(Math.abs(overShort))}`} strong />
        </div>
      </Card>

      <Card className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-6">
        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">{t("deposit_word")}</p>
        <Row label={t("starting_bills_returned")} value={fmt(count.starting_bills_returned ?? count.starting_bills)} />
        <Row label={t("coins_remaining")} value={fmt(count.coins_remaining ?? count.total_coins_counted)} />
        <div className="border-t border-primary/20 pt-1"><Row label={t("deposit_amount")} value={fmt(deposit)} strong /></div>
      </Card>

      {hasDenoms && (
        <Card className="rounded-2xl border-border p-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("denomination_breakdown")}</p>
          <div className="grid grid-cols-2 gap-x-6">
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-primary">{t("starting_word")}</p>
              {[...BILL_DENOMS, ...COIN_DENOMS].map((dn) => (
                <div key={dn.key} className="flex justify-between border-b border-border/60 py-1.5 text-sm">
                  <span className="font-semibold text-foreground">{dn.label}</span>
                  <span className="tabular-nums text-muted-foreground">{fmt(sd[dn.key] || 0)}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-primary">{t("closing_word")}</p>
              {[...BILL_DENOMS, ...COIN_DENOMS].map((dn) => (
                <div key={dn.key} className="flex justify-between border-b border-border/60 py-1.5 text-sm">
                  <span className="font-semibold text-foreground">{dn.label}</span>
                  <span className="tabular-nums text-muted-foreground">{fmt(cd[dn.key] || 0)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {count.notes && <NoteBlock text={count.notes} />}
    </div>
  );
}
